import { useMemo } from 'react'

import { useModals, useSetState, useShowModal } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CopyLinkModal from '@modals/CopyLinkModal'
import CreateOrEditImageModal from '@modals/CreateOrEditImageModal'
import DownloadMediaModal from '@modals/DownloadMediaModal'
import MediaReviewerModal from '@modals/MediaReviewerModal'
import SelectSMSRecipientModal from '@modals/SelectSMSRecipientModal'

import type { CampaignModel, ImageModel, ProviderModel } from '@models/types'

import PageContent from './components/PageContent'

export type PageContextPayload = {
  callbacks: {
    showCreateOrEditImageModal: () => void,
  },
}

const defaultState = {
  showCopyLinkModal: false,
  showCreateOrEditImageModal: false,
  showDownloadMediaModal: false,
  showMediaReviewerModal: false,
  showSelectSMSRecipientModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CopyLinkModal: {
      closeModal: () => setState({ showCopyLinkModal: false }),
    },
    CreateOrEditImageModal: {
      closeModal: () => setState({ showCreateOrEditImageModal: false }),
      createImage: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateImage: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    DownloadMediaModal: {
      closeModal: () => setState({ showDownloadMediaModal: false }),
      downloadImages: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateAgency: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    MediaReviewerModal: {
      closeModal: () => setState({ showMediaReviewerModal: false }),
    },
    SelectSMSRecipientModal: {
      closeModal: () => setState({ showSelectSMSRecipientModal: false }),
      shareMobileLink: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCopyLinkModal,
    showCreateOrEditImageModal,
    showDownloadMediaModal,
    showMediaReviewerModal,
    showSelectSMSRecipientModal,
  } = state

  const modalContext = useModals({ setState })
  const { callbacks: { setModalData } } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      showCopyLinkModal: (payload: { image: ImageModel }) => {
        setModalData('CopyLinkModal', payload)
        setState({ showCopyLinkModal: true })
      },
      showCreateOrEditImageModal: (payload: { image: ImageModel }) => {
        setModalData('CreateOrEditImageModal', payload)
        setState({ showCreateOrEditImageModal: true })
      },
      showDownloadMediaModal: (payload: { image: ImageModel }) => {
        setModalData('DownloadMediaModal', payload)
        setState({ showDownloadMediaModal: true })
      },
      showMediaReviewerModal: (payload: { campaign: CampaignModel, provider: ProviderModel }) => {
        setModalData('MediaReviewerModal', payload)
        setState({ showMediaReviewerModal: true })
      },
      showSelectSMSRecipientModal: (payload: { campaign: CampaignModel }) => {
        setModalData('SelectSMSRecipientModal', payload)
        setState({ showSelectSMSRecipientModal: true })
      },
    },
  }), [])

  useShowModal({
    modalKey: 'MediaReviewerModal',
    options: {
      // @ts-expect-error TS2322
      callbacks: pageContext.callbacks,
    },
  })

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent />

        {/*
         // @ts-expect-error TS2741 */}
        <CopyLinkModal
          callbacks={callbacks('CopyLinkModal', setState)}
          showModal={showCopyLinkModal}
        />

        <CreateOrEditImageModal
          callbacks={callbacks('CreateOrEditImageModal', setState)}
          showModal={showCreateOrEditImageModal}
        />

        <DownloadMediaModal
          // @ts-expect-error TS2322
          callbacks={callbacks('DownloadMediaModal', setState)}
          showModal={showDownloadMediaModal}
        />

        <MediaReviewerModal
          callbacks={callbacks('MediaReviewerModal', setState)}
          showModal={showMediaReviewerModal}
        />

        <SelectSMSRecipientModal
          // @ts-expect-error TS2322
          callbacks={callbacks('SelectSMSRecipientModal', setState)}
          showModal={showSelectSMSRecipientModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
